// ./src/components/features/EventTickets/TicketSaleDrawer/TicketSaleDrawer.tsx
//
// "Sell at the door" — the box-office sale flow over the reusable selling engine
// (TicketOrderService: reserve → confirm). Three steps:
//   1) pick ticket types (live availability, qty steppers) → reserve (holds seats)
//   2) buyer (address-book pick or new person) + per-guest names (names mode) + payment
//   3) done — issued ticket codes.
// The reservation holds inventory for N minutes (countdown shown); closing before
// payment releases the hold. Concurrency-safe / no overselling is enforced server-side.
// ⚠ #282 — every price here is the SERVER's, for the date and the member on the form (`TicketOrderService.availability`):
//   a tier, a "buy more" deal or a member price never reads off the ticket type. A seller quotes these to a walk-in.
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, Group, Text, Box, NumberInput, TextInput, Badge, Divider, Paper, ThemeIcon, Loader, Alert, SegmentedControl, Input, Button, Checkbox } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { EMPTY_ADDRESS, addressMissingParts, ticketsCost, toAddressInput, toAnswerAddress, type AnswerAddress } from "@em/api";

import {
  TicketOrderService,
  TicketOrder,
  TicketAvailability,
  TicketTypeService,
  TicketType,
  TICKET_SALES_MODE,
  TICKET_PAYMENT_METHOD,
  TICKET_PAYMENT_METHOD_VALUES,
  TICKET_PAYMENT_METHOD_LABELS,
  EventService,
  EventTicket,
  listAllPages,
} from "@/utils/api";
import { AppDrawer, AppIcon, DiscardGuard } from "@/components/generic";
import { PersonPicker } from "@/components/features/EventGuests/PersonPicker";
import { AddressAnswerInput } from "@/components/features/EventGuests/AddressAnswerInput";
import { getFormErrors } from "@/utils";
import { useToast, useEventTimeSlots, useFormSnapshot } from "@/hooks";
import { RECURRING_SCHEDULE_MODE, bookableSlotOptions, isSlotBookable } from "@/utils/eventDates";
import { SessionPicker } from "@em/ui/SessionPicker";

export interface TicketSaleDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  onCompleted?: () => void;
}

type Step = "select" | "buyer" | "done";

interface AvailType extends TicketType {
  available?: number | null;
  soldOut?: boolean;
  /** #282 — null when availability could not be read: no price is shown rather than a wrong one. */
  effectivePrice?: number | null;
  costs?: number[] | null;
  atThisPrice?: number | null;
  nextPrice?: number | null;
}

const RESERVE_MINUTES = 15;

// The two names the confirm step refuses under that have a control of their own here:
// `buyer` (name/contact missing or invalid) and `method` (unknown payment method). The
// step-1 refusals are named `items`/`eventId` — there is no single control for a basket of
// quantity steppers, so those stay generic and are printed in the footer alert.
const KNOWN_FORM_FIELDS = ["buyer", "buyerAddress", "method", "membershipNumber"] as const;

const fmtMoney = (amount?: number | string | null, currency?: string | null): string => {
  if (amount == null || amount === "") {
    return "—";
  }

  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return "—";
  }

  const v = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${v}` : v;
};

/** Whether the server stated what `n` of this type cost. */
const costIsKnown = (tt: AvailType, n: number): boolean =>
  tt.effectivePrice != null && (!tt.costs?.length || n <= tt.costs.length);

const mmss = (secs: number): string => {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  const r = s % 60;

  return `${m}:${String(r).padStart(2, "0")}`;
};

const TicketSaleDrawerComponent: React.FC<TicketSaleDrawerProps> = ({ opened, onClose, eventId, onCompleted }) => {
  const { t } = useTranslation(["ticketSales", "common"]);
  const toast = useToast();

  const [step, setStep] = useState<Step>("select");
  // Memberships (owner, 2026-10-13) — a member at the door: members-only tickets and member prices. Checked on reserve.
  const [membershipNumber, setMembershipNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<AvailType[]>([]);
  const [guestTracking, setGuestTracking] = useState<string | null>(null);
  // ROADMAP A4 — a recurring event sells ONE date per door sale: stock and capacity are per date, so
  // availability is read for the chosen date and every ticket held carries it.
  const [scheduleMode, setScheduleMode] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [dateId, setDateId] = useState<string | null>(null);
  const dateIdRef = useRef<string | null>(null);
  // #282 — the number the list was last priced for, and the newest read: a late answer must not overwrite it.
  const pricedForRef = useRef("");
  const catalogReadRef = useRef(0);
  const reservingRef = useRef(false);
  const { slots } = useEventTimeSlots(opened ? eventId : null);
  const [qty, setQty] = useState<Record<string, number>>({});

  const [reserving, setReserving] = useState(false);
  const [order, setOrder] = useState<TicketOrder | null>(null);
  const [tickets, setTickets] = useState<EventTicket[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const [buyerPersonId, setBuyerPersonId] = useState<string | null>(null);
  const [buyerFirstName, setBuyerFirstName] = useState("");
  const [buyerLastName, setBuyerLastName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  // A2e #87 — optional at the box office (owner, 2026-10-02: never block a sale at the door); whole when given.
  const [buyerAddress, setBuyerAddress] = useState<AnswerAddress>({ ...EMPTY_ADDRESS });
  const [buyerIsAttendee, setBuyerIsAttendee] = useState(false);
  const [guests, setGuests] = useState<Record<string, { firstName?: string; lastName?: string; email?: string }>>({});
  const [method, setMethod] = useState<string>(TICKET_PAYMENT_METHOD.CASH);

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);

  const reservedUntilRef = useRef<string | null>(null);
  const orderRef = useRef<TicketOrder | null>(null);
  orderRef.current = order;

  const collectNames = guestTracking === "names";
  const recurring = scheduleMode === RECURRING_SCHEDULE_MODE;
  const dateOptions = useMemo(() => bookableSlotOptions(slots, timezone), [slots, timezone]);
  const expired = step === "buyer" && secondsLeft <= 0;

  // A sale in progress — tickets picked, a date, the buyer typed — is work: Esc, a click outside or the X ask before
  // throwing it away (#123). A quantity set back to 0 is nothing; a COMPLETED sale has nothing left to lose.
  const saleForm = {
    qty: Object.fromEntries(Object.entries(qty).filter(([, n]) => n > 0)),
    dateId,
    membershipNumber,
    buyerPersonId,
    buyerFirstName,
    buyerLastName,
    buyerEmail,
    buyerPhone,
    buyerAddress,
    buyerIsAttendee,
    guests,
    method,
  };
  const { isDirty: saleChanged, seedFrom, clear } = useFormSnapshot(saleForm);

  // ── Load ticket types + availability + event when opening ──
  // `quiet` keeps the list on screen: a member's prices arrive as the seller leaves the box, on the way to Reserve.
  const loadCatalog = useCallback(async (quiet = false) => {
    catalogReadRef.current += 1;

    const read = catalogReadRef.current;
    const number = pricedForRef.current || null;

    if (!quiet) {
      setLoading(true);
    }

    setError(null);

    const [typesRes, asked, eventRes] = await Promise.all([
      listAllPages(TicketTypeService.list, {
        eventId: [eventId],
        salesMode: [TICKET_SALES_MODE.ON_SALE, TICKET_SALES_MODE.DOOR_ONLY],
        isAddOn: false,
      }),
      TicketOrderService.availability({ eventId, eventTimeSlotId: dateIdRef.current, membershipNumber: number }),
      EventService.get(eventId),
    ]);

    // A number not valid here: its refusal lands on its field, and the list stays priced for nobody.
    const numberRefused = !!number && !!getFormErrors(asked, KNOWN_FORM_FIELDS).fields.membershipNumber;
    const availRes = numberRefused ? await TicketOrderService.availability({ eventId, eventTimeSlotId: dateIdRef.current }) : asked;

    if (read !== catalogReadRef.current) {
      return;
    }

    // A read that lands after the seller pressed Reserve is about a list no longer in question: the reserve's answer stands.
    if (!reservingRef.current && !orderRef.current) {
      if (numberRefused) {
        setError(asked);
      } else if (availRes.code !== OP_RESULT_CODES.OK) {
        // ⚠ Never quietly: every row would read "Unlimited" with no price.
        setError(availRes);
      }
    }

    const availByType: Record<string, TicketAvailability> = {};
    if (availRes.code === OP_RESULT_CODES.OK && Array.isArray(availRes.data)) {
      (availRes.data as TicketAvailability[]).forEach((a) => {
        if (a.ticketTypeId) {
          availByType[a.ticketTypeId] = a;
        }
      });
    }

    if (typesRes.code === OP_RESULT_CODES.OK && Array.isArray(typesRes.data)) {
      const merged: AvailType[] = (typesRes.data as TicketType[]).map((tt) => ({
        ...tt,
        available: tt.id ? availByType[tt.id]?.available ?? null : null,
        soldOut: tt.id ? availByType[tt.id]?.soldOut ?? false : false,
        effectivePrice: tt.id ? availByType[tt.id]?.effectivePrice ?? null : null,
        costs: tt.id ? availByType[tt.id]?.costs ?? null : null,
        atThisPrice: tt.id ? availByType[tt.id]?.atThisPrice ?? null : null,
        nextPrice: tt.id ? availByType[tt.id]?.nextPrice ?? null : null,
      }));
      setTypes(merged);
    } else {
      setError(typesRes);
    }

    if (eventRes.code === OP_RESULT_CODES.OK && Array.isArray(eventRes.data)) {
      const ev = (eventRes.data as Array<{ guestTracking?: string | null; scheduleMode?: string | null; timezone?: string | null }>)[0];
      setGuestTracking(ev?.guestTracking ?? null);
      setScheduleMode(ev?.scheduleMode ?? null);
      setTimezone(ev?.timezone ?? null);
    }

    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setStep("select");
    setQty({});
    dateIdRef.current = null;
    setDateId(null);
    // ⚠ The next sale is the next customer: a number left here would price them as this member.
    setMembershipNumber("");
    pricedForRef.current = "";
    setOrder(null);
    setTickets([]);
    setBuyerPersonId(null);
    setBuyerFirstName("");
    setBuyerLastName("");
    setBuyerEmail("");
    setBuyerPhone("");
    setBuyerAddress({ ...EMPTY_ADDRESS });
    setBuyerIsAttendee(false);
    setGuests({});
    setMethod(TICKET_PAYMENT_METHOD.CASH);
    seedFrom({
      qty: {},
      dateId: null,
      membershipNumber: "",
      buyerPersonId: null,
      buyerFirstName: "",
      buyerLastName: "",
      buyerEmail: "",
      buyerPhone: "",
      buyerAddress: { ...EMPTY_ADDRESS },
      buyerIsAttendee: false,
      guests: {},
      method: TICKET_PAYMENT_METHOD.CASH,
    });
    setError(null);
    reservedUntilRef.current = null;

    loadCatalog();
  }, [opened, loadCatalog, clear, seedFrom]);

  // ── Reservation countdown ──
  useEffect(() => {
    if (step !== "buyer" || !reservedUntilRef.current) {
      return;
    }

    const tick = () => {
      const until = reservedUntilRef.current ? new Date(reservedUntilRef.current).getTime() : 0;
      setSecondsLeft(Math.round((until - Date.now()) / 1000));
    };

    tick();
    const id = window.setInterval(tick, 1000);

    return () => window.clearInterval(id);
  }, [step]);

  const items = useMemo(
    () => Object.entries(qty).filter(([, n]) => n > 0).map(([ticketTypeId, quantity]) => ({ ticketTypeId, quantity })),
    [qty]
  );
  const totalQty = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  // The server's own costs, so it adds up as the reserve will charge. Null = a price that could not be read, or a count past
  // what the server sent (50): no estimate at a till, where a tier could change past there. The next step has the total.
  const subtotal = useMemo(() => {
    let sum = 0;

    for (const tt of types) {
      const n = tt.id ? qty[tt.id] || 0 : 0;

      if (n > 0) {
        if (!costIsKnown(tt, n)) {
          return null;
        }

        sum += ticketsCost(tt, n);
      }
    }

    return Math.round(sum * 100) / 100;
  }, [types, qty]);
  const currency = types.find((tt) => tt.currencyCode)?.currencyCode || null;

  // Picking another date re-reads availability FOR THAT DATE and clears the cart — a quantity chosen
  // against one date's stock means nothing on another.
  const handleDateChange = useCallback(
    (next: string | null) => {
      dateIdRef.current = next;
      setDateId(next);
      setQty({});
      loadCatalog();
    },
    [loadCatalog]
  );

  // A type the host restricted to one date is only offered on that date.
  const visibleTypes = useMemo(
    () => (recurring ? (dateId ? types.filter((tt) => !tt.eventTimeSlotId || tt.eventTimeSlotId === dateId) : []) : types),
    [recurring, dateId, types]
  );

  const applyMembership = useCallback(() => {
    const next = membershipNumber.trim();

    if (next === pricedForRef.current) {
      return;
    }

    pricedForRef.current = next;
    void loadCatalog(true);
  }, [membershipNumber, loadCatalog]);

  const setTypeQty = useCallback((id: string, value: number, max: number | null) => {
    const clamped = Math.max(0, max != null ? Math.min(value, max) : value);
    setQty((prev) => ({ ...prev, [id]: clamped }));
  }, []);

  // ── Reserve (step 1 → 2) ──
  const handleReserve = useCallback(async () => {
    if (items.length === 0) {
      return;
    }

    reservingRef.current = true;
    setReserving(true);
    setError(null);

    const result = await TicketOrderService.reserve({
      eventId,
      saleChannel: "door",
      reservationMinutes: RESERVE_MINUTES,
      items,
      eventTimeSlotId: recurring ? dateId : null,
      membershipNumber: membershipNumber.trim() || null,
    });

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      const o = (result.data as TicketOrder[])[0];
      setOrder(o);
      setTickets(o.tickets || []);
      reservedUntilRef.current = o.reservedUntil ?? null;
      setStep("buyer");
    } else {
      setError(result);
    }

    reservingRef.current = false;
    setReserving(false);
  }, [eventId, items, recurring, dateId, membershipNumber]);

  // ── Confirm (step 2 → done) ──
  const handleConfirm = useCallback(async () => {
    if (!order?.id) {
      return;
    }

    setConfirming(true);
    setError(null);

    // In names mode the buyer (when attending) takes the first ticket server-side;
    // send attendee details for the remaining tickets.
    const guestList = collectNames
      ? tickets
          .filter((tk, idx) => tk.id && !(buyerIsAttendee && idx === 0))
          .map((tk) => {
            const g = guests[tk.id as string] || {};
            return {
              ticketId: tk.id as string,
              firstName: g.firstName?.trim() || null,
              lastName: g.lastName?.trim() || null,
              email: g.email?.trim() || null,
            };
          })
          .filter((g) => g.firstName || g.lastName || g.email)
      : undefined;

    const result = await TicketOrderService.confirm({
      orderId: order.id,
      method,
      buyerIsAttendee: collectNames ? buyerIsAttendee : false,
      buyer: {
        personId: buyerPersonId,
        firstName: buyerFirstName.trim() || null,
        lastName: buyerLastName.trim() || null,
        email: buyerEmail.trim() || null,
        phone: buyerPhone.trim() || null,
        address: toAddressInput(buyerAddress),
      },
      guests: guestList,
    });

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      const o = (result.data as TicketOrder[])[0];
      setOrder(o);
      setTickets(o.tickets || tickets);
      reservedUntilRef.current = null;
      setStep("done");
      onCompleted?.();
    } else {
      setError(result);
    }

    setConfirming(false);
  }, [order, method, collectNames, tickets, guests, buyerIsAttendee, buyerPersonId, buyerFirstName, buyerLastName, buyerEmail, buyerPhone, buyerAddress, onCompleted]);

  // ── Close — release a still-held reservation ──
  const handleClose = useCallback(() => {
    const current = orderRef.current;
    if (step === "buyer" && current?.id) {
      TicketOrderService.cancel(current.id).catch(() => undefined);
    }

    onClose();
  }, [step, onClose]);

  const startOver = useCallback(async () => {
    const current = orderRef.current;

    // A read still on its way counted this hold, and would end the wait below.
    catalogReadRef.current += 1;
    setOrder(null);
    setTickets([]);
    reservedUntilRef.current = null;
    setError(null);
    setStep("select");
    // The tickets step waits too — Reserve on the old stock would be refused while this hold still counts.
    setLoading(true);

    // The availability read waits for the release, or it would still count this hold.
    if (current?.id) {
      await TicketOrderService.cancel(current.id).catch(() => undefined);
    }

    loadCatalog();
  }, [loadCatalog]);

  const canConfirm = !!buyerPersonId || !!buyerFirstName.trim() || !!buyerLastName.trim();

  // Surface server errors through the drawer shell: the buyer/payment ones land on their own
  // control, everything else ("Only N left", "Event not found", …) shows above the buttons.
  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(error, KNOWN_FORM_FIELDS),
    [error]
  );

  // ── Footer per step ──
  const footer = useMemo(() => {
    if (step === "select") {
      return (
        <Group justify="space-between" w="100%">
          <Text size="md" fw={600}>
            {t("ticketSales:sale.subtotal", { defaultValue: "Subtotal" })}: {fmtMoney(subtotal, currency)}
          </Text>
          <Group gap="sm">
            {/* Through the drawer's own close attempt, like its X and Esc: a sale in progress asks first (#124). */}
            <DiscardGuard>
              {(leave) => (
                <Button variant="default" size="md" onClick={() => leave()}>
                  {t("common:buttons.cancel", { defaultValue: "Cancel" })}
                </Button>
              )}
            </DiscardGuard>
            <Button size="md" onClick={handleReserve} loading={reserving} disabled={totalQty === 0 || (recurring && !dateId)}>
              {t("ticketSales:sale.reserve", { defaultValue: "Reserve & continue" })}
            </Button>
          </Group>
        </Group>
      );
    }

    if (step === "buyer") {
      return (
        <Group justify="flex-end" w="100%" gap="sm">
          {/* Back releases the hold, like Cancel: it used to leave this reservation held while "Reserve & continue" held a
              second, so door stock read short for up to 15 minutes (#126). The picks and the buyer's details stay. */}
          <Button variant="default" size="md" onClick={startOver} disabled={confirming}>
            {t("common:buttons.back", { defaultValue: "Back" })}
          </Button>
          <Button size="md" onClick={handleConfirm} loading={confirming} disabled={!canConfirm || expired}>
            {t("ticketSales:sale.complete", { defaultValue: "Complete sale" })}
          </Button>
        </Group>
      );
    }

    return (
      <Group justify="flex-end" w="100%">
        <Button size="md" onClick={onClose}>
          {t("common:buttons.done", { defaultValue: "Done" })}
        </Button>
      </Group>
    );
  }, [step, subtotal, currency, totalQty, recurring, dateId, reserving, confirming, canConfirm, expired, handleReserve, handleConfirm, startOver, onClose, t]);

  return (
    <AppDrawer
      opened={opened}
      onClose={handleClose}
      title={t("ticketSales:sale.title", { defaultValue: "Sell at the door" })}
      actionType="custom"
      customActions={footer}
      isDirty={step !== "done" && saleChanged}
      errors={genericErrors}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      size="lg"
    >
      <Stack gap="md">
        {loading && (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        )}

        {/* ── STEP 1: select tickets ── */}
        {!loading && step === "select" && (
          <Stack gap="sm">
            {recurring && (
              // ROADMAP A4 — one date per door sale, picked day-then-time (a day can hold several sessions).
              <SessionPicker
                label={t("ticketSales:sale.date", { defaultValue: "Date" })}
                required
                multiple={false}
                timezone={timezone}
                options={slots.filter((s) => !!s.id && isSlotBookable(s)).map((s) => ({ id: s.id as string, startsAt: s.startsAt, endsAt: s.endsAt, isAllDay: s.isAllDay, name: s.name }))}
                value={dateId ? [dateId] : []}
                onChange={(ids) => handleDateChange(ids[0] ?? null)}
                labels={{ noSessions: t("ticketSales:sale.noDates", { defaultValue: "No upcoming dates" }) }}
              />
            )}

            {recurring && !dateId && dateOptions.length > 0 && (
              <Text size="md" c="dimmed">
                {t("ticketSales:sale.dateFirst", { defaultValue: "Choose a date to see what is left for it." })}
              </Text>
            )}

            {(!recurring || dateId) && visibleTypes.length === 0 && (
              <Text size="md" c="dimmed">
                {t("ticketSales:sale.noTypes", { defaultValue: "No ticket types are on sale for this event yet." })}
              </Text>
            )}

            {visibleTypes.map((tt) => {
              const id = tt.id as string;
              const soldOut = !!tt.soldOut;
              const left = tt.available;
              const priced = tt.effectivePrice != null;
              const picked = qty[id] || 0;
              const lineKnown = costIsKnown(tt, picked);
              const lineCost = lineKnown ? ticketsCost(tt, picked) : 0;
              // A pick that crosses a tier, or earns a deal, is not the price above × the count: say what it is.
              const lineDiffers = picked > 0 && lineKnown && Math.abs(lineCost - picked * Number(tt.effectivePrice)) >= 0.005;

              return (
                <Paper key={id} withBorder radius="md" p="md" opacity={soldOut ? 0.6 : 1}>
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="md" fw={600} lineClamp={1}>
                        {tt.name}
                      </Text>
                      <Group gap="xs" mt={2}>
                        <Text size="md">{fmtMoney(tt.effectivePrice, tt.currencyCode)}</Text>
                        {priced && (
                          left != null ? (
                            <Badge size="sm" variant="light" color={soldOut ? "red" : "green"}>
                              {soldOut
                                ? t("ticketSales:sale.soldOut", { defaultValue: "Sold out" })
                                : t("ticketSales:sale.left", { defaultValue: "{{n}} left", n: left })}
                            </Badge>
                          ) : (
                            <Badge size="sm" variant="light" color="gray">
                              {t("ticketSales:sale.unlimited", { defaultValue: "Unlimited" })}
                            </Badge>
                          )
                        )}
                      </Group>
                      {priced && tt.atThisPrice != null && tt.nextPrice != null && (
                        <Text size="md" c="dimmed" mt={2}>
                          {t("ticketSales:sale.tierNote", {
                            defaultValue: "{{left}} left at this price, then {{next}}",
                            left: tt.atThisPrice,
                            next: fmtMoney(tt.nextPrice, tt.currencyCode),
                          })}
                        </Text>
                      )}
                      {lineDiffers && (
                        <Text size="md" fw={600} mt={2}>
                          {t("ticketSales:sale.lineCost", {
                            defaultValue: "{{n}} for {{amount}}",
                            n: picked,
                            amount: fmtMoney(lineCost, tt.currencyCode),
                          })}
                        </Text>
                      )}
                    </Box>
                    <NumberInput
                      value={qty[id] || 0}
                      onChange={(v) => setTypeQty(id, Number(v) || 0, left ?? null)}
                      min={0}
                      max={left ?? undefined}
                      // "Sold in multiples of": the arrows move a whole step (the server refuses any other count).
                      step={tt.isPayWhatYouWant ? 1 : Math.max(1, Math.floor(Number(tt.orderIncrement) || 1))}
                      disabled={soldOut}
                      clampBehavior="strict"
                      size="md"
                      w={110}
                      style={{ flexShrink: 0 }}
                    />
                  </Group>
                </Paper>
              );
            })}
            {types.some((tt) => tt.membershipMode && tt.membershipMode !== "any") && (
              <TextInput
                size="md"
                label={t("ticketSales:sale.membership", { defaultValue: "Membership number" })}
                description={t("ticketSales:sale.membershipHint", {
                  defaultValue: "For members-only tickets and member prices. The prices above change once the number is in.",
                })}
                placeholder={t("ticketSales:sale.optional", { defaultValue: "Optional" })}
                value={membershipNumber}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setMembershipNumber(v);
                }}
                onBlur={applyMembership}
                error={fieldErrors.membershipNumber}
              />
            )}
          </Stack>
        )}

        {/* ── STEP 2: buyer + guests + payment ── */}
        {!loading && step === "buyer" && (
          <Stack gap="md">
            <Alert
              variant="light"
              color={expired ? "red" : "blue"}
              icon={<AppIcon iconName={expired ? "AlertTriangle" : "Clock"} size={18} />}
            >
              {expired
                ? t("ticketSales:sale.expired", { defaultValue: "This reservation expired. Start over to sell these tickets." })
                : t("ticketSales:sale.held", { defaultValue: "{{n}} ticket(s) held · {{time}} left", n: tickets.length, time: mmss(secondsLeft) })}
              {expired && (
                <Box mt="xs">
                  <Button variant="light" color="red" size="sm" onClick={startOver}>
                    {t("ticketSales:sale.startOver", { defaultValue: "Start over" })}
                  </Button>
                </Box>
              )}
            </Alert>

            <Text size="md" fw={600}>
              {t("ticketSales:sale.buyer", { defaultValue: "Buyer" })}
            </Text>
            <PersonPicker
              value={buyerPersonId}
              onSelect={(person) => {
                setBuyerPersonId(person?.id ?? null);
                if (person) {
                  setBuyerFirstName(person.firstName ?? "");
                  setBuyerLastName(person.lastName ?? "");
                  setBuyerEmail(person.email ?? "");
                  setBuyerPhone(person.phone ?? "");
                  // The contact's address on file — but only a WHOLE one: a contact with just a country (bulk edit sets
                  // that alone) would otherwise be refused at confirm, and staff would have to clear a field they never
                  // touched to finish a sale at the door.
                  const onFile = toAnswerAddress(person);
                  setBuyerAddress(addressMissingParts(onFile).length === 0 ? onFile : { ...EMPTY_ADDRESS });
                }
              }}
              label={t("ticketSales:sale.pickContact", { defaultValue: "Pick from address book (optional)" })}
              size="md"
              clearable
              // "Buyer name (or an existing contact) is required" / "Invalid buyer contact" —
              // the whole buyer block's refusal, folded onto the control that leads it.
              error={fieldErrors.buyer}
            />
            <Group grow align="flex-start">
              <TextInput
                label={t("ticketSales:sale.firstName", { defaultValue: "First name" })}
                value={buyerFirstName}
                onChange={(e) => { const v = e.currentTarget.value; setBuyerFirstName(v); setBuyerPersonId(null); }}
                size="md"
              />
              <TextInput
                label={t("ticketSales:sale.lastName", { defaultValue: "Last name" })}
                value={buyerLastName}
                onChange={(e) => { const v = e.currentTarget.value; setBuyerLastName(v); setBuyerPersonId(null); }}
                size="md"
              />
            </Group>
            <Group grow align="flex-start">
              <TextInput label={t("ticketSales:sale.email", { defaultValue: "Email" })} value={buyerEmail} onChange={(e) => setBuyerEmail(e.currentTarget.value)} size="md" />
              <TextInput label={t("ticketSales:sale.phone", { defaultValue: "Phone" })} value={buyerPhone} onChange={(e) => setBuyerPhone(e.currentTarget.value)} size="md" />
            </Group>
            <AddressAnswerInput
              label={t("ticketSales:sale.address", { defaultValue: "Address (optional)" })}
              description={t("ticketSales:sale.addressHint", {
                defaultValue: "Printed on the receipt and saved to the buyer's contact. Street, city and country, or leave it blank.",
              })}
              value={buyerAddress}
              onChange={setBuyerAddress}
              error={fieldErrors.buyerAddress}
            />

            {collectNames && tickets.length > 0 && (
              <>
                <Divider label={t("ticketSales:sale.attendees", { defaultValue: "Attendees" })} labelPosition="left" />
                <Checkbox
                  label={t("ticketSales:sale.buyerAttending", { defaultValue: "Buyer is also attending (uses the first ticket)" })}
                  checked={buyerIsAttendee}
                  onChange={(e) => setBuyerIsAttendee(e.currentTarget.checked)}
                  size="md"
                />
                {tickets.map((tk, idx) => {
                  if (buyerIsAttendee && idx === 0) {
                    return (
                      <Text key={tk.id} size="md" c="dimmed">
                        {t("ticketSales:sale.ticketForBuyer", { defaultValue: "Ticket {{n}} · {{code}} — buyer", n: idx + 1, code: tk.ticketCode })}
                      </Text>
                    );
                  }

                  const g = (tk.id && guests[tk.id]) || {};
                  const setG = (patch: { firstName?: string; lastName?: string; email?: string }) => {
                    if (tk.id) {
                      setGuests((prev) => ({ ...prev, [tk.id as string]: { ...prev[tk.id as string], ...patch } }));
                    }
                  };

                  return (
                    <Box key={tk.id}>
                      <Text size="sm" c="dimmed" mb={4}>
                        {t("ticketSales:sale.ticketN", { defaultValue: "Ticket {{n}} · {{code}}", n: idx + 1, code: tk.ticketCode })}
                      </Text>
                      <Group grow align="flex-start">
                        <TextInput
                          placeholder={t("ticketSales:sale.firstName", { defaultValue: "First name" })}
                          value={g.firstName || ""}
                          onChange={(e) => { const v = e.currentTarget.value; setG({ firstName: v }); }}
                          size="md"
                        />
                        <TextInput
                          placeholder={t("ticketSales:sale.lastName", { defaultValue: "Last name" })}
                          value={g.lastName || ""}
                          onChange={(e) => { const v = e.currentTarget.value; setG({ lastName: v }); }}
                          size="md"
                        />
                      </Group>
                      <TextInput
                        mt={4}
                        placeholder={t("ticketSales:sale.emailOptional", { defaultValue: "Email (for online access, optional)" })}
                        value={g.email || ""}
                        onChange={(e) => { const v = e.currentTarget.value; setG({ email: v }); }}
                        size="md"
                      />
                    </Box>
                  );
                })}
              </>
            )}

            <Divider />
            <Input.Wrapper
              label={t("ticketSales:sale.payment", { defaultValue: "Payment method" })}
              error={fieldErrors.method}
            >
              <SegmentedControl
                value={method}
                onChange={setMethod}
                data={TICKET_PAYMENT_METHOD_VALUES.map((v) => ({ value: v, label: t(`ticketSales:method.${v}`, { defaultValue: TICKET_PAYMENT_METHOD_LABELS[v] }) }))}
                size="md"
                mt={4}
                fullWidth
              />
            </Input.Wrapper>

            <Group justify="space-between">
              <Text size="md" c="dimmed">
                {t("ticketSales:sale.total", { defaultValue: "Total" })}
              </Text>
              <Text size="md" fw={700}>
                {fmtMoney(order?.totalAmount, order?.currencyCode)}
              </Text>
            </Group>
          </Stack>
        )}

        {/* ── STEP 3: done ── */}
        {step === "done" && (
          <Stack gap="md" align="center" py="md">
            <ThemeIcon color="green" size={54} radius="xl" variant="light">
              <AppIcon iconName="Check" size={30} />
            </ThemeIcon>
            <Text size="md" fw={600}>
              {t("ticketSales:sale.doneTitle", { defaultValue: "{{n}} ticket(s) sold", n: tickets.length })}
            </Text>
            <Stack gap="xs" w="100%">
              {tickets.map((tk) => (
                <Paper key={tk.id} withBorder radius="md" p="sm">
                  <Group justify="space-between">
                    <Text size="md" fw={600} ff="monospace">
                      {tk.ticketCode}
                    </Text>
                    <Text size="md" c="dimmed">
                      {tk.holderName || fmtMoney(tk.price, tk.currencyCode)}
                    </Text>
                  </Group>
                </Paper>
              ))}
            </Stack>
            <Button
              variant="light"
              size="md"
              leftSection={<AppIcon iconName="Printer" size={16} />}
              onClick={() => toast.info(t("ticketSales:actions.printComingSoon", { defaultValue: "Printing tickets is coming soon." }))}
            >
              {t("ticketSales:sale.print", { defaultValue: "Print tickets" })}
            </Button>
          </Stack>
        )}
      </Stack>
    </AppDrawer>
  );
};

export const TicketSaleDrawer = React.memo(TicketSaleDrawerComponent);
